import { useEffect, useState, useContext, useRef } from "react";
import MasterEntrySelectorModal from "./MasterEntrySelectorModal";
import * as XLSX from "xlsx";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import { CodeLegendContext } from "../../common/contexts/CodeLegendContext";
import {
  getCodeLegendById,
  saveCodeLegend,
  saveMasterCodeLegendEntry,
} from "../../api/APIHelpers";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";
import { Delete as DeleteIcon, Link, Add } from "@mui/icons-material";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  IconButton,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  TablePagination,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { TenantContext } from "../../common/contexts/TenantContext";
import Autocomplete from "@mui/material/Autocomplete";

const entryFields = [
  { key: "Code", label: "Code", type: "number", editable: true },
  { key: "Description", label: "Description", type: "text", editable: true },
  { key: "AlarmStateId", label: "Alarm State", type: "number", editable: true },
  { key: "SendAlerts", label: "Send Alerts", type: "checkbox", editable: true },
  { key: "IsMechId", label: "Is Mechanical", type: "number", editable: true },
];

const CodeLegendEditor = ({ codeLegendId, initialLegend, onSaveAsNew }) => {
  const [legend, setLegend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [masterModalOpen, setMasterModalOpen] = useState(false);
  const [masterModalEntryIdx, setMasterModalEntryIdx] = useState(null);
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  const [confirmCreateIdx, setConfirmCreateIdx] = useState(null);
  const [saveAsNewDialogOpen, setSaveAsNewDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const lookupValues = useContext(LookupValueContext);
  const { masterEntries, refreshCodeLegendContext } =
    useContext(CodeLegendContext);
  const { setInfoMessages } = useContext(AlertsContext);
  const fileInputRef = useRef();
  const [mastersToSave, setMastersToSave] = useState([]);
  const [saving, setSaving] = useState(false);
  const tenantInfo = useContext(TenantContext);

  const alarmStateObj = lookupValues?.find(
    (lv) => lv.LookupName === "AlarmState"
  );
  const alarmStates =
    alarmStateObj?.Values?.map((value) => ({
      id: value.LookupValueId,
      name: value.DisplayName,
    })) || [];

  const isMechObj = lookupValues?.find((lv) => lv.LookupName === "IsMech");
  const isMechOptions =
    isMechObj?.Values?.map((value) => ({
      id: value.LookupValueId,
      name: value.DisplayName,
    })) || [];

  useEffect(() => {
    if (initialLegend) {
      setLegend(initialLegend);
      setLoading(false);
    } else {
      setLoading(true);
      getCodeLegendById(codeLegendId)
        .then((response) => {
          setLegend(response.data);
          setLoading(false);
        })
        .catch((err) => {
          setError("Failed to load Code Legend");
          setLoading(false);
        });
    }
  }, [codeLegendId, initialLegend]);

  const handleDeleteEntry = (entryIdx) => {
    setLegend((prev) => {
      const newEntries = prev.Entries.filter((_, idx) => idx !== entryIdx);
      return { ...prev, Entries: newEntries };
    });
  };

  const handleLegendFieldChange = (field, value) => {
    setLegend((prev) => ({ ...prev, [field]: value }));
  };

  const handleEntryFieldChange = (entryIdx, field, value) => {
    setLegend((prev) => {
      const newEntries = [...prev.Entries];
      let updatedEntry = { ...newEntries[entryIdx], [field]: value };
      newEntries[entryIdx] = updatedEntry;
      return { ...prev, Entries: newEntries };
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress />
        <Typography>Loading Code Legend...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color='error'>{error}</Typography>
      </Box>
    );
  }

  const handleAddEntry = () => {
    setLegend((prev) => ({
      ...prev,
      Entries: [
        ...prev.Entries,
        {
          Code: 0,
          Description: "",
          AlarmStateId: null,
          SendAlerts: false,
          IsMechId: null,
          // Not editable fields
          CodeLegendEntryId: 0,
          CodeLegendId: prev.CodeLegendId,
          CodeLegendMasterEntryId: null,
          NonConformingCode: "",
          ComponentId: null,
          SystemId: null,
        },
      ],
    }));
  };

  // Excel Export
  const handleExportExcel = () => {
    const visibleFields = entryFields.map((f) => f.key);
    const data = legend.Entries.map((entry) => {
      const row = {};
      visibleFields.forEach((key) => {
        if (key === "AlarmStateId") {
          const found = alarmStates.find(
            (opt) => opt.id === entry.AlarmStateId
          );
          row[key] = found ? found.name : "";
        } else if (key === "IsMechId") {
          const found = isMechOptions.find((opt) => opt.id === entry.IsMechId);
          row[key] = found ? found.name : "";
        } else {
          row[key] = entry[key] ?? "";
        }
      });
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Entries");
    XLSX.writeFile(workbook, "CodeLegendEntries.xlsx");
  };

  // Excel Import
  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const imported = XLSX.utils.sheet_to_json(sheet);
      // Only visible fields, others nulled/zeroed
      const visibleFields = entryFields.map((f) => f.key);
      const newEntries = imported.map((row) => {
        const entry = {};
        visibleFields.forEach((key) => {
          if (key === "AlarmStateId") {
            const found = alarmStates.find((opt) => opt.name === row[key]);
            entry[key] = found ? found.id : null;
          } else if (key === "IsMechId") {
            const found = isMechOptions.find((opt) => opt.name === row[key]);
            entry[key] = found ? found.id : null;
          } else {
            entry[key] =
              row[key] ??
              (entryFields.find((f) => f.key === key).type === "number"
                ? 0
                : null);
          }
        });
        // Null/zero non-visible fields
        entry.CodeLegendEntryId = 0;
        entry.CodeLegendId = legend.CodeLegendId;
        entry.CodeLegendMasterEntryId = null;
        entry.NonConformingCode = "";
        entry.ComponentId = null;
        entry.SystemId = null;
        return entry;
      });
      setLegend((prev) => ({ ...prev, Entries: newEntries }));
    };
    reader.readAsArrayBuffer(file);
  };

  // Handler for opening modal
  const handleOpenMasterModal = (entryIdx) => {
    setMasterModalEntryIdx(entryIdx);
    setMasterModalOpen(true);
  };

  // Handler for selecting master entry
  const handleSelectMasterEntry = (masterEntryId) => {
    if (masterModalEntryIdx != null) {
      setLegend((prev) => {
        const newEntries = [...prev.Entries];
        newEntries[masterModalEntryIdx] = {
          ...newEntries[masterModalEntryIdx],
          CodeLegendMasterEntryId: masterEntryId,
        };
        return { ...prev, Entries: newEntries };
      });
    }
    setMasterModalOpen(false);
    setMasterModalEntryIdx(null);
  };

  // Handler for opening create master entry modal
  const handleOpenCreateMasterModal = (entryIdx) => {
    setConfirmCreateIdx(entryIdx);
    setConfirmCreateOpen(true);
  };

  const findMasterEntryByDescription = (desc) => {
    return masterEntries.find(
      (me) =>
        me.Description.toUpperCase() === desc.toUpperCase() ||
        me.AltDescriptions.some(
          (ad) => ad.Description.toUpperCase() === desc.toUpperCase()
        )
    );
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      setInfoMessages({
        type: "snackbar",
        message: "Saving Code Legend...",
        severity: "info",
        open: true,
      });
      await saveCodeLegend(legend);
      setInfoMessages({
        type: "snackbar",
        message: "Code Legend saved successfully.",
        severity: "success",
        open: true,
      });
      for (const masterEntry of mastersToSave) {
        await saveMasterCodeLegendEntry(masterEntry);
      }
      setMastersToSave([]);
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `Error saving Code Legend: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setSaving(false);
      await refreshCodeLegendContext();
    }
  };

  const handleSaveAsNew = () => {
    setNewTitle(legend.Title || "");
    setSaveAsNewDialogOpen(true);
  };

  const handleConfirmSaveAsNew = async () => {
    setSaveAsNewDialogOpen(false);
    setSaving(true);
    try {
      setInfoMessages({
        type: "snackbar",
        message: "Saving Code Legend as New...",
        severity: "info",
        open: true,
      });
      const newLegendEntries = legend.Entries.map((entry) => ({
        ...entry,
        CodeLegendEntryId: 0,
      }));
      const newLegend = {
        ...legend,
        CodeLegendId: 0,
        Title: newTitle,
        Entries: newLegendEntries,
      };
      const response = await saveCodeLegend(newLegend);
      await refreshCodeLegendContext();
      newLegend.CodeLegendId = response.data.CodeLegendId;
      setLegend(newLegend);
      setInfoMessages({
        type: "snackbar",
        message: "Code Legend saved as New successfully.",
        severity: "success",
        open: true,
      });
      if (onSaveAsNew && typeof onSaveAsNew === "function") {
        onSaveAsNew(newLegend);
      }
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `Error saving Code Legend as New: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCreate = async () => {
    if (confirmCreateIdx != null) {
      const entry = legend.Entries[confirmCreateIdx];
      const newMasterEntry = {
        CodeLegendMasterEntryId: 0,
        Description: entry.Description,
        AlarmStateId: entry.AlarmStateId,
        IsMechId: entry.IsMechId,
        SendAlerts: entry.SendAlerts,
        AltDescriptions: [],
        TenantId: tenantInfo.currentTenantId,
      };
      console.log(newMasterEntry);
      try {
        const result = await saveMasterCodeLegendEntry(newMasterEntry);
        setInfoMessages({
          type: "snackbar",
          message: "Standard Code Description created successfully.",
          severity: "success",
          open: true,
        });
        const newMasterId = result.data.CodeLegendMasterEntryId;
        // link to entry
        setLegend((prev) => {
          const newEntries = [...prev.Entries];
          newEntries[confirmCreateIdx] = {
            ...newEntries[confirmCreateIdx],
            CodeLegendMasterEntryId: newMasterId,
          };
          return { ...prev, Entries: newEntries };
        });
      } catch (err) {
        setInfoMessages({
          type: "snackbar",
          message: `Error creating Standard Code Description: ${extractApiErrorMessage(err)}`,
          severity: "error",
          open: true,
        });
      }
    }
    setConfirmCreateOpen(false);
    setConfirmCreateIdx(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h6' gutterBottom>
        Edit Code Legend
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          label='Title'
          value={legend.Title || ""}
          onChange={(e) => handleLegendFieldChange("Title", e.target.value)}
          sx={{ minWidth: 400 }}
          disabled={saving}
        />
        <FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography>Is Shareable</Typography>
            <Checkbox
              checked={legend.IsShareable}
              onChange={(e) =>
                handleLegendFieldChange("IsShareable", e.target.checked)
              }
              color='primary'
              disabled={saving}
            />
          </Box>
        </FormControl>
      </Box>
      <Typography variant='h6' gutterBottom>
        Edit Entries
      </Typography>
      <Stack
        direction='row'
        spacing={2}
        sx={{ mb: 2 }}
        style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant='outlined'
          onClick={handleExportExcel}
          sx={{ ml: 2 }}
          disabled={saving}>
          Export to Excel
        </Button>
        <Button
          variant='outlined'
          component='label'
          sx={{ ml: 2 }}
          disabled={saving}>
          Import from Excel
          <input
            type='file'
            accept='.xlsx,.xls'
            hidden
            ref={fileInputRef}
            onChange={handleImportExcel}
          />
        </Button>
      </Stack>
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {entryFields.map((field) => (
                <TableCell
                  key={field.key}
                  sx={
                    field.key === "Code"
                      ? { width: 80, minWidth: 60, maxWidth: 100 }
                      : field.key === "Description"
                        ? { width: 350, minWidth: 250, maxWidth: 600 }
                        : {}
                  }>
                  {field.label}
                </TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {legend.Entries.slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            ).map((entry, idx) => {
              const realIdx = page * rowsPerPage + idx;
              return (
                <TableRow
                  key={entry.CodeLegendEntryId || realIdx}
                  sx={
                    entry.CodeLegendMasterEntryId == null
                      ? findMasterEntryByDescription(entry.Description) == null
                        ? { backgroundColor: "#ffebee" }
                        : { backgroundColor: "#ffffebff" }
                      : {}
                  }>
                  {entryFields.map((field) => (
                    <TableCell
                      key={field.key}
                      sx={
                        field.key === "Code"
                          ? { width: 80, minWidth: 60, maxWidth: 100 }
                          : field.key === "Description"
                            ? { width: 350, minWidth: 250, maxWidth: 600 }
                            : {}
                      }>
                      {field.editable ? (
                        field.key === "AlarmStateId" ? (
                          <Select
                            value={entry.AlarmStateId ?? ""}
                            onChange={(e) =>
                              handleEntryFieldChange(
                                realIdx,
                                "AlarmStateId",
                                e.target.value
                              )
                            }
                            size='small'
                            sx={{ minWidth: 120 }}
                            disabled={saving}>
                            {alarmStates.map((state) => (
                              <MenuItem key={state.id} value={state.id}>
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : field.key === "IsMechId" ? (
                          <Select
                            value={entry.IsMechId ?? ""}
                            onChange={(e) =>
                              handleEntryFieldChange(
                                realIdx,
                                "IsMechId",
                                e.target.value
                              )
                            }
                            size='small'
                            sx={{ minWidth: 120 }}
                            disabled={saving}>
                            {isMechOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : field.key === "Description" ? (
                          <Autocomplete
                            options={masterEntries}
                            getOptionKey={(option) =>
                              option.CodeLegendMasterEntryId
                            }
                            getOptionLabel={(option) =>
                              typeof option === "string"
                                ? option
                                : option?.Description
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.Description === value
                            }
                            value={entry.Description}
                            onChange={(_, newValue) => {
                              // When user selects from dropdown, assign master entry
                              if (
                                typeof newValue === "object" &&
                                newValue !== null
                              ) {
                                setLegend((prev) => {
                                  const newEntries = [...prev.Entries];
                                  newEntries[realIdx] = {
                                    ...newEntries[realIdx],
                                    Description: newValue.Description,
                                    CodeLegendMasterEntryId:
                                      newValue.CodeLegendMasterEntryId,
                                    AlarmStateId: newValue.AlarmStateId,
                                    IsMechId: newValue.IsMechId,
                                    SendAlerts: newValue.SendAlerts,
                                  };
                                  return { ...prev, Entries: newEntries };
                                });
                              } else {
                                // Free text entry or cleared
                                handleEntryFieldChange(
                                  realIdx,
                                  "Description",
                                  typeof newValue === "string" ? newValue : ""
                                );
                              }
                            }}
                            onInputChange={(_, newInputValue) => {
                              handleEntryFieldChange(
                                realIdx,
                                "Description",
                                newInputValue
                              );
                            }}
                            freeSolo
                            disabled={saving}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={option.CodeLegendMasterEntryId}>
                                {option.Description}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size='small'
                                sx={{ minWidth: 80 }}
                              />
                            )}
                          />
                        ) : field.type === "checkbox" ? (
                          <Checkbox
                            checked={!!entry[field.key]}
                            onChange={(e) =>
                              handleEntryFieldChange(
                                realIdx,
                                field.key,
                                e.target.checked
                              )
                            }
                            color='primary'
                            disabled={saving}
                          />
                        ) : (
                          <TextField
                            value={entry[field.key] ?? ""}
                            type='text'
                            onChange={(e) => {
                              let value = e.target.value;
                              if (field.type === "number") {
                                // Only allow digits
                                value = value.replace(/\D/g, "");
                                value = value === "" ? "" : parseInt(value);
                              }
                              handleEntryFieldChange(realIdx, field.key, value);
                            }}
                            size='small'
                            sx={{ minWidth: 80 }}
                            disabled={saving}
                          />
                        )
                      ) : (
                        (entry[field.key] ?? "")
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Tooltip title='Delete entry'>
                      <IconButton
                        aria-label='delete-entry'
                        color='error'
                        onClick={() => handleDeleteEntry(realIdx)}
                        disabled={saving}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    {entry.CodeLegendMasterEntryId == null &&
                      findMasterEntryByDescription(entry.Description) ==
                        null && (
                        <>
                          <Tooltip title='Link to standard description'>
                            <IconButton
                              aria-label='link-standard'
                              onClick={() => handleOpenMasterModal(realIdx)}
                              disabled={saving}>
                              <Link />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Create standard description'>
                            <IconButton
                              aria-label='create-standard'
                              onClick={() =>
                                handleOpenCreateMasterModal(realIdx)
                              }
                              disabled={saving}>
                              <Add />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    {entry.CodeLegendMasterEntryId == null &&
                      findMasterEntryByDescription(entry.Description) !=
                        null && (
                        <Tooltip title='Link to standard description'>
                          <IconButton
                            aria-label='link-standard'
                            onClick={() => handleOpenMasterModal(realIdx)}
                            disabled={saving}>
                            <Link />
                          </IconButton>
                        </Tooltip>
                      )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={legend.Entries.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage='Rows per page:'
        />
      </TableContainer>
      {/* Master Entry Selector Modal */}
      <MasterEntrySelectorModal
        open={masterModalOpen}
        onClose={() => {
          setMasterModalOpen(false);
          setMasterModalEntryIdx(null);
        }}
        masterEntries={masterEntries}
        onSelect={handleSelectMasterEntry}
        preSelectedId={
          masterModalOpen && masterModalEntryIdx != null
            ? (() => {
                const entry = legend.Entries[masterModalEntryIdx];
                const match = masterEntries.find(
                  (me) =>
                    me.Description.toUpperCase() ===
                    entry.Description.toUpperCase()
                );
                return match ? match.CodeLegendMasterEntryId : null;
              })()
            : null
        }
        onLink={({ masterEntryId, altDescription }) => {
          if (
            mastersToSave.find(
              (me) => me.CodeLegendMasterEntryId === masterEntryId
            )
          ) {
            const masterEntry = mastersToSave.find(
              (me) => me.CodeLegendMasterEntryId === masterEntryId
            );
            masterEntry.AltDescriptions = [
              ...masterEntry.AltDescriptions,
              {
                Description: altDescription,
                CodeLegendMasterEntryId: masterEntry.CodeLegendMasterEntryId,
                CodeLegendMasterEntryAltDescId: 0,
              },
            ];
            setMastersToSave([
              ...mastersToSave.filter(
                (me) => me.CodeLegendMasterEntryId !== masterEntryId
              ),
              masterEntry,
            ]);
          } else {
            const masterEntry = masterEntries.find(
              (me) => me.CodeLegendMasterEntryId === masterEntryId
            );
            masterEntry.AltDescriptions = [
              ...masterEntry.AltDescriptions,
              {
                Description: altDescription,
                CodeLegendMasterEntryId: masterEntry.CodeLegendMasterEntryId,
                CodeLegendMasterEntryAltDescId: 0,
              },
            ];
            setMastersToSave([...mastersToSave, masterEntry]);
          }
        }}
      />
      {/* Create Master Entry Confirmation Dialog */}
      <Dialog
        open={confirmCreateOpen}
        onClose={() => setConfirmCreateOpen(false)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle>Create Standard Description</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to create a new Standard Code Description from this Code
            Legend Entry?
          </Typography>
          {confirmCreateIdx != null && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2'>
                <strong>Description:</strong>{" "}
                {legend.Entries[confirmCreateIdx].Description}
              </Typography>
              <Typography variant='body2'>
                <strong>Alarm State:</strong>{" "}
                {alarmStates.find(
                  (as) =>
                    as.id === legend.Entries[confirmCreateIdx].AlarmStateId
                )?.name || ""}
              </Typography>
              <Typography variant='body2'>
                <strong>Is Mechanical:</strong>{" "}
                {isMechOptions.find(
                  (im) => im.id === legend.Entries[confirmCreateIdx].IsMechId
                )?.name || ""}
              </Typography>
              <Typography variant='body2'>
                <strong>Send Alerts:</strong>{" "}
                {legend.Entries[confirmCreateIdx].SendAlerts ? "Yes" : "No"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmCreate}
            variant='contained'
            color='primary'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Save As New Dialog */}
      <Dialog
        open={saveAsNewDialogOpen}
        onClose={() => setSaveAsNewDialogOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Save Code Legend as New</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please enter a title for the new Code Legend:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label='Title'
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveAsNewDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmSaveAsNew}
            variant='contained'
            color='primary'
            disabled={!newTitle.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "end" }} gap={2}>
        <Button
          variant='outlined'
          color='primary'
          onClick={handleAddEntry}
          disabled={saving}>
          Add Entry
        </Button>
        {legend.CodeLegendId !== 0 && (
          <Tooltip title='This will create a new Code Legend with the current entries. If you are editing from the Tag Editor, this will also assign the new code legend to the tag.'>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSaveAsNew}
              disabled={saving}>
              {saving ? "Saving..." : "Save as New"}
            </Button>
          </Tooltip>
        )}
        <Tooltip title='Save changes to the current Code Legend. This will update all tags using this Code Legend to reflect any changes made.'>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSaveChanges}
            disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default CodeLegendEditor;
